// === grandmaster-frontend/frontend/src/components/LichessCallback.js ===
// (MODIFIED - Call saveTokenAndFetchUser which now handles fetching user data)
import React, { useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { OAuth2AuthCodePKCE } from "@bity/oauth2-auth-code-pkce";
import { AuthContext, API_BASE_URL } from "../App";

function LichessCallback() {
  const navigate = useNavigate();
  const { saveToken } = useContext(AuthContext); // Use the context function
  const effectRan = useRef(false);
  const redirectUri = `${window.location.protocol}//${
    window.location.hostname
  }${
    window.location.port ? `:${window.location.port}` : ""
  }/auth-lichess-callback`;

  // Initialize outside useEffect to avoid re-creation on every render
  const authClient = useRef(
    new OAuth2AuthCodePKCE({
      authorizationUrl: "https://lichess.org/oauth",
      tokenUrl: "https://lichess.org/api/token",
      clientId: "grandmasterai.com", // Your registered client ID
      redirectUrl: redirectUri,
      scopes: ["email:read"],
      onInvalidGrant: (error) => {
        console.error("Lichess Invalid Grant Error:", error);
        navigate("/login?error=invalid_grant");
      },
      // Consider adding onAccessTokenExpiry and onInvalidToken if using refresh tokens later
    })
  ).current;

  useEffect(() => {
    if (effectRan.current || process.env.NODE_ENV !== "development") {
      // Skip second run in StrictMode development or run normally in production
    } else {
      effectRan.current = true; // Mark first run in development
      return; // Skip execution on first render in dev StrictMode
    }

    const processAuth = async () => {
      console.log("Processing Lichess callback...");
      try {
        // Check if we are returning from the auth server with a code
        if (authClient.isReturningFromAuthServer()) {
          console.log("Exchanging Lichess code for token...");
          const lichessTokenResponse = await authClient.getAccessToken();
          console.log("Received Lichess token response:", lichessTokenResponse);

          if (lichessTokenResponse?.token?.value) {
            console.log("Sending Lichess token to backend...");
            const backendResponse = await fetch(
              `${API_BASE_URL}/auth-lichess-callback`, // Use dynamic base URL
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  lichess_token: lichessTokenResponse.token.value,
                }),
              }
            );

            if (backendResponse.ok) {
              const backendData = await backendResponse.json(); // Expect { access_token: "...", user: {...} }
              console.log(
                "Received JWT and User Data from backend:",
                backendData
              );
              // saveToken (which is saveTokenAndFetchUser) handles storing token AND fetching/storing user
              saveToken(backendData.access_token);
              console.log("Login successful, navigating to dashboard...");
              navigate("/dashboard");
            } else {
              const errorText = await backendResponse.text();
              console.error(
                "Backend login failed:",
                backendResponse.status,
                errorText
              );
              navigate(
                `/login?error=backend_login_failed&status=${backendResponse.status}`
              );
            }
          } else {
            console.error("Did not receive a valid token value from Lichess.");
            navigate("/login?error=lichess_token_missing");
          }
        } else {
          console.warn(
            "Not returning from Lichess auth server (no code found). Redirecting to login."
          );
          // This case might happen if the user navigates directly to the callback URL
          navigate("/login");
        }
      } catch (error) {
        console.error("Lichess OAuth Callback Error:", error);
        const errorMessage = error?.error || error?.message || "oauth_error";
        // Extract specific error if available from OAuth2AuthCodePKCE
        const errorDescription = error?.error_description || "No description";
        console.error(
          `OAuth Error Details: ${errorMessage} - ${errorDescription}`
        );
        navigate(`/login?error=${encodeURIComponent(errorMessage)}`);
      }
    };

    processAuth();
    // Add authClient to dependencies, though it should be stable due to useRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, saveToken, authClient]);

  return (
    <div style={{ padding: "20px", textAlign: "center" }}>
      Processing Lichess login... Please wait.
    </div>
  );
}

export default LichessCallback;
